import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LabelValueComponent, UIBlockerService } from '@howdeniberia/core-front';
import { forkJoin, Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { IResponseMessage } from 'src/app/core/models/api/sane-project/arpa-model';
import { CalculatorProjectService } from 'src/app/core/services/api/calculator/calculator-project.service';
import { BonusPipe } from 'src/app/core/services/api/shared/bonus.pipe';
import { SharedService } from 'src/app/core/services/api/shared/shared.service';
import { normalizeTraceHtml, toBooleanStrict } from 'src/app/shared/utils';
import { ProjectViewModel } from '../../models/project.viewmodel';
import { CalculatorMapper } from '../mappers/calculator.mapper';
import { CalculatorProductViewModel, itPresets } from '../models/calculator-product.viewmodel';
import { NumberMaxDirective } from '../models/number-max.directive';

@Component({
  selector: 'howden-calculator-it',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatDividerModule,
    LabelValueComponent,
    BonusPipe,
    NumberMaxDirective
  ],
  templateUrl: './calculator-it.component.html',
  styleUrls: ['./calculator-it.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalculatorItComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly uiBlocker = inject(UIBlockerService);
  private readonly calcSrv = inject(CalculatorProjectService);
  private readonly sharedSrv = inject(SharedService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly sanitizer = inject(DomSanitizer);

  form: FormGroup;

  readonly itPmElite = new CalculatorProductViewModel(itPresets['pmElite']);
  readonly itPmCoeg = new CalculatorProductViewModel(itPresets['pmCoeg']);
  readonly itSurnePlus = new CalculatorProductViewModel(itPresets['surnePlus']);
  readonly itSurneVip = new CalculatorProductViewModel(itPresets['surneVip']);
  readonly itPrebal = new CalculatorProductViewModel(itPresets['prebal']);

  project!: ProjectViewModel;

  private readonly destroy$ = new Subject<void>();

  private readonly vmMap = {
    pmElite: this.itPmElite,
    pmCoeg: this.itPmCoeg,
    surnePlus: this.itSurnePlus,
    surneVip: this.itSurneVip,
    prebal: this.itPrebal
  } as const;

  compareByIdNumber = this.calcSrv.compareByIdNumber.bind(this.calcSrv);
  compareByIdBoolean = this.calcSrv.compareByIdBoolean.bind(this.calcSrv);

  canSeeTrace = false;
  canChargeITPMCoeg = false;

  constructor() {
    this.form = this.fb.group({
      itPmElite: this.itPmElite.form,
      itPmCoeg: this.itPmCoeg.form,
      itSurnePlus: this.itSurnePlus.form,
      itSurneVip: this.itSurneVip.form,
      itPrebal: this.itPrebal.form
    });

    this.canSeeTrace = this.sharedSrv.getCanSeeTrace();
    this.canChargeITPMCoeg = this.sharedSrv.getCanSeeCoegTI();

    this.sharedSrv.canSeeTrace$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.canSeeTrace = v;
        this.cdr.markForCheck();
      });

    this.sharedSrv.canSeePmTICoeg$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.canChargeITPMCoeg = v;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public shouldShowZeroState(vm: CalculatorProductViewModel): boolean {
    return !vm.hasAnyPositiveNumeric();
  }

  public showTrace(vm: CalculatorProductViewModel): boolean {
    return this.canSeeTrace && vm.hasAnyPositiveNumeric();
  }

  public getTraceHtml(vm: CalculatorProductViewModel): SafeHtml | '' {
    if (!this.showTrace(vm) || !vm.trace) { return ''; }
    const normalized = normalizeTraceHtml(vm.trace);
    return this.sanitizer.bypassSecurityTrustHtml(normalized);
  }

  public getMessages(vm: CalculatorProductViewModel): IResponseMessage[] {
    return vm.hasAnyPositiveNumeric() ? (vm.messages ?? []) : [];
  }

  public initFromViewModel(vm: ProjectViewModel['calculatorIT']): void {
    const model = this.sharedSrv.getModel();
    if (!model) { throw new Error('ProjectViewModel not available'); }
    this.project = model;

    this.canSeeTrace = this.sharedSrv.getCanSeeTrace();
    this.determineVisibility();

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        const src = vm[key];
        child.form.patchValue(src.form.getRawValue(), { emitEvent: false });

        child.bonus = src.bonus ?? 0;
        child.chargeable = !!src.chargeable;
        child.productName = src.productName ?? '';
        child.trace = src.trace ?? '';
        child.maximumCapital = src.maximumCapital ?? 0;
        child.hasTelesubscription = !!src.hasTelesubscription;
        child.telesubscriptionDirect = !!src.telesubscriptionDirect;
        child.telesubscriptionSingleQuestion = !!src.telesubscriptionSingleQuestion;
        child.summaryCapitals = src.summaryCapitals ?? '';
        child.messages = Array.isArray(src.messages) ? [...src.messages] : [];
      });

    const hasLifeCtrl = this.itPrebal.form.get('itPrebalHasLife');
    if (hasLifeCtrl) {
      const normalized = toBooleanStrict(hasLifeCtrl.value);
      if (normalized !== hasLifeCtrl.value) {
        hasLifeCtrl.patchValue(normalized, { emitEvent: false });
      }
    }

    this.uiBlocker.block();
    forkJoin({
      franchise: this.calcSrv.getSelectFranchise(),
      duration: this.calcSrv.getSelectDuration(),
      hasLifeOptions: this.calcSrv.getSChargeable()
    })
      .pipe(
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe(({ franchise, duration, hasLifeOptions }) => {
        this.itPmElite.setMasterValues('itPmEliteFranchise', franchise);
        this.itPmCoeg.setMasterValues('itPmCoegFranchise', franchise);
        this.itSurnePlus.setMasterValues('itSurnePlusFranchise', franchise);
        this.itSurneVip.setMasterValues('itSurneVipFranchise', franchise);
        this.itPrebal.setMasterValues('itPrebalFranchise', franchise);

        this.itPmElite.setMasterValues('itPmEliteDuration', duration);
        this.itPmCoeg.setMasterValues('itPmCoegDuration', duration);
        this.itSurnePlus.setMasterValues('itSurnePlusDuration', duration);
        this.itSurneVip.setMasterValues('itSurneVipDuration', duration);
        this.itPrebal.setMasterValues('itPrebalDuration', duration);

        const normalizedHasLife = (hasLifeOptions ?? []).map((opt: any) => ({
          ...opt,
          id: toBooleanStrict(opt.id)
        }));
        this.itPrebal.setMasterValues('itPrebalHasLife', normalizedHasLife);
      });

    this.setupListeners();

    this.onInputChangeITPmElite();

    if (this.canChargeITPMCoeg) {
      this.onInputChangeITPmCoeg();
    }

    this.onInputChangeITSurnePlus();
    this.onInputChangeITSurneVip();
    this.onInputChangeITPrebal();
  }

  //Visibilidad COEG basada en el backend (getClient/getProjectById.canSeePmTICoeg)

  public determineVisibility(): void {
    this.canChargeITPMCoeg = this.sharedSrv.getCanSeeCoegTI();
    this.cdr.markForCheck();
  }

  public flushToViewModel(): void {
    const vm = this.sharedSrv.getModel()!.calculatorIT;

    (Object.entries(this.vmMap) as Array<[keyof typeof this.vmMap, CalculatorProductViewModel]>)
      .forEach(([key, child]) => {
        if (key === 'prebal') {
          const ctrl = child.form.get('itPrebalHasLife');
          if (ctrl) {
            const normalized = toBooleanStrict(ctrl.value);
            if (normalized !== ctrl.value) {
              ctrl.patchValue(normalized, { emitEvent: false });
            }
          }
        }

        const tgt = vm[key];
        tgt.form.patchValue(child.form.getRawValue(), { emitEvent: false });

        tgt.bonus = child.bonus ?? 0;
        tgt.chargeable = !!child.chargeable;
        tgt.productName = child.productName ?? '';
        tgt.trace = child.trace ?? '';
        tgt.maximumCapital = child.maximumCapital ?? 0;
        tgt.hasTelesubscription = child.hasTelesubscription ?? false;
        tgt.telesubscriptionDirect = child.telesubscriptionDirect ?? false;
        tgt.telesubscriptionSingleQuestion = child.telesubscriptionSingleQuestion ?? false;
        tgt.summaryCapitals = child.summaryCapitals ?? '';
        tgt.messages = Array.isArray(child.messages) ? [...child.messages] : [];
      });
  }

  private setupListeners(): void {
    const hook = (child: CalculatorProductViewModel, recalcFn: () => void) => {
      child.changes$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => recalcFn());
    };

    hook(this.itPmElite, () => this.onInputChangeITPmElite());

    hook(this.itPmCoeg, () => {
      if (this.canChargeITPMCoeg) {
        this.onInputChangeITPmCoeg();
      }
    });

    hook(this.itSurnePlus, () => this.onInputChangeITSurnePlus());
    hook(this.itSurneVip, () => this.onInputChangeITSurneVip());
    hook(this.itPrebal, () => this.onInputChangeITPrebal());
  }

  private recalc(obs$: Observable<unknown>): void {
    this.uiBlocker.block();
    obs$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uiBlocker.unblock();
          this.cdr.markForCheck();
        })
      )
      .subscribe();
  }

  public onInputChangeITPmElite(): void {
    this.recalc(
      CalculatorMapper.calcItPmElite(
        this.project,
        this.itPmElite,
        req => this.calcSrv.resultBonusITPmElite(req)
      )
    );
  }

  public onInputChangeITPmCoeg(): void {
    this.recalc(
      CalculatorMapper.calcItPmCoeg(
        this.project,
        this.itPmCoeg,
        req => this.calcSrv.resultBonusITPmCoeg(req)
      )
    );
  }

  public onInputChangeITSurnePlus(): void {
    this.recalc(
      CalculatorMapper.calcItSurnePlus(
        this.project,
        this.itSurnePlus,
        req => this.calcSrv.resultBonusITSurnePlus(req)
      )
    );
  }

  public onInputChangeITSurneVip(): void {
    this.recalc(
      CalculatorMapper.calcItSurneVip(
        this.project,
        this.itSurneVip,
        req => this.calcSrv.resultBonusITSurneVip(req)
      )
    );
  }

  public onInputChangeITPrebal(): void {
    const ctrl = this.itPrebal.form.get('itPrebalHasLife');
    if (ctrl) {
      const normalized = toBooleanStrict(ctrl.value);
      if (normalized !== ctrl.value) {
        ctrl.patchValue(normalized, { emitEvent: false });
      }
    }

    this.recalc(
      CalculatorMapper.calcItPrebal(
        this.project,
        this.itPrebal,
        req => this.calcSrv.resultBonusITPrebal(req)
      )
    );
  }
}
